/**
 * VoiceAgentController — single class owning the full lifecycle of a
 * Realtime-Voice session.
 *
 * Replaces the previous useTschaukoSession React-hook (700 lines of
 * refs/useCallbacks). The hook-style version produced closure races (e.g.
 * two near-simultaneous open() calls both seeing status='idle' from
 * stale closures and minting two parallel sessions). A plain class
 * eliminates that whole class of bug. The shared RealtimeAgentCore owns the
 * synchronous opening guard and the canonical session/PTT/tool state.
 *
 * React integrates via a tiny adapter (useVoiceAgent) backed by
 * useSyncExternalStore — the React docs' canonical recipe for binding
 * mutable external state.
 *
 * Responsibilities the controller owns:
 *   - Session state machine (idle / connecting / ready / speaking / …)
 *   - Daily per-device cap (localStorage)
 *   - Server-side mint via /api/voice-token
 *   - WebRTC PeerConnection + audio playback
 *   - GA Realtime DataChannel event handling
 *   - User-location context (continuous GPS + URL override)
 *   - Tool-call dispatch + __app_command__ extraction → CommandRegistry
 *   - Push-to-talk (mic enable/disable + barge-in cancel)
 *   - Voice-interrupt bus (other audio sources stop the bot)
 *   - Transcript history (user + bot bubbles)
 *
 * Designed reusable: takes a config (instructions/tools/voice/…) so it
 * can serve any Realtime-Voice persona, not just Dr. Tschauko.
 */

import { subscribeVoiceInterrupt } from '../voiceInterrupt'
import { audioManager } from '@/lib/audio/audioManager'
import type { CommandRegistry } from './types'
import {
  RealtimeAgentCore,
  type RealtimeSessionStatus,
} from '../../../libs/realtime-agent-web-core/dist/index.js'
import { loadHistory, saveHistory, bubblesForPriming, clearHistory } from './chatHistory'
import { trackEvent } from './progressTracker'
import {
  AGENTOS_AVATAR_RUNTIME_PROTOCOL,
  classifyAvatarFunctionCall,
  AgentOSAvatarStateMachine,
  isAgentOSAvatarAffectFollowup,
  normalizeAgentOSAvatarAffect,
  type AgentOSAvatarAffect,
  type AgentOSAvatarAffectIntensity,
  type AgentOSAvatarProjection,
  type AgentOSAvatarRole,
  type AgentOSAvatarRuntimeEventKind,
} from './AgentOSAvatarRuntime'
import {
  shouldAcceptProviderSpeechStarted,
} from './VoiceAgentPttPolicy'
import {
  classifyVoiceAgentOpenError,
  type VoiceAgentErrorRecovery,
} from './VoiceAgentOpenError'
import {
  guideOpeningInstructions,
  normalizeGuideEntryContext,
  normalizeResolvedGuideEntryContext,
  type GuideEntryContext,
  type GuideEntryContextResolver,
  type ResolvedGuideEntryContext,
} from './GuideEntryContext'

// ─── Types ──────────────────────────────────────────────────────────────────

export type SessionStatus = RealtimeSessionStatus

export interface TranscriptEntry {
  id: string
  speaker: 'user' | 'bot'
  text: string
  partial?: boolean
  ts: number
}

export interface VoiceAgentConfig {
  /** System prompt / instructions for the LLM. */
  instructions: string
  /** Spoken greeting line the bot says at session start. */
  greeting: string
  /** Sprach-Anweisung + passende Begrüßung, beim ÖFFNEN der Sitzung gelesen
   *  (Alex 29.07.). Nicht beim Bauen des Controllers: den gibt es einmal pro
   *  Seitenaufruf, die Sprache kann der Besucher jederzeit umstellen. Liefert
   *  `directive: ''`, bleibt alles wie bisher. */
  getLanguage?: () => { directive: string; greeting: string; name: string }
  /** Tools schema for the GA Realtime session.update. */
  tools: ReadonlyArray<Record<string, unknown>>
  /** OpenAI Realtime voice id (e.g. 'verse'). */
  voice?: string
  /** Hard session-duration cap in seconds. */
  maxSessionSeconds?: number
  /** Per-device per-day session count cap. Infinity in dev by default. */
  maxSessionsPerDay?: number
  /** Optional app-command-bus for tool-result side-effects. */
  registry?: CommandRegistry
  /** Override the server-side mint endpoint (default /api/voice-token). */
  mintUrl?: string
  /** Override the server-side tool-call endpoint (default /api/voice-tool). */
  toolUrl?: string
  /** Override the server-side usage-report endpoint (default
   *  /api/voice-usage). Called on session-end with the cumulative token
   *  totals so AiApi's master cost-tracker can attribute the spend. */
  usageUrl?: string
  /** localStorage key for the daily session counter. */
  storageKey?: string
  /** Optional async hook that returns an extra context line appended to
   *  the system prompt right before mint. The tschepp-ar-web app passes
   *  its USER-KONTEXT GPS-zone resolver here. Pure TS callback — keeps
   *  the controller framework-agnostic and app-decoupled. */
  getContextLine?: () => Promise<string>
  /** Trusted, server-backed resolver for contextual guide entries. The UI
   *  supplies only source identity and opening policy; factual text must be
   *  resolved here. Required only for `contextual_briefing` entries. */
  resolveEntryContext?: GuideEntryContextResolver
  /** Optional source of truth for the user's current GPS position.
   *  When provided, the controller reads from this on every tool-call
   *  INSTEAD of running its own watchPosition — so the voice-agent and
   *  the map share a single GPS singleton (e.g. LocationService). Set
   *  this to null/undefined to fall back to the internal watcher. */
  getPosition?: () => { lat: number; lon: number } | null
  /** Optional sink for internal errors (mint failure, SDP exchange
   *  failure, realtime-event errors). The app routes these to its own
   *  observability layer (e.g. log-api). When omitted, errors only end
   *  up in console + the user-facing snapshot.errorMessage. */
  onError?: (event: string, err: unknown, ctx?: Record<string, unknown>) => void
  /** Optional sink for non-error lifecycle events (status flips, tool
   *  calls, GPS state). Lets the app push them to log-api alongside
   *  errors so post-session debug doesn't need screenshots. */
  onInfo?: (event: string, ctx?: Record<string, unknown>) => void
}

export interface SessionTokenUsage {
  audioInputTokens: number
  audioOutputTokens: number
  textInputTokens: number
  textOutputTokens: number
  cachedInputTokens: number
}

export interface VoiceAgentSnapshot {
  status: SessionStatus
  transcript: TranscriptEntry[]
  errorMessage: string | null
  /** Recovery action for the current error; null outside error state. */
  errorRecovery: VoiceAgentErrorRecovery | null
  elapsedSeconds: number
  isMicActive: boolean
  focusedKnowledgeId: number | null
  /**
   * Ort, den der Agent gerade zeigt — und WOZU.
   *
   * Zwei Kommandos setzen ihn, mit verschiedener Absicht (#464 vs. focus_map):
   *   `map`    — der Besucher hat nach dem ORT gefragt („wo ist das
   *              Besucherzentrum?", `where_is`). Antwort ist ein Kartenbild.
   *   `detail` — der Erzaehler hat eine Station ERWAEHNT (`focus_map`).
   *              Antwort ist ihre Beschreibung.
   * Ohne dieses Feld sieht die Oberflaeche nur „ein Ort ist gesetzt" und
   * muesste raten — und beantwortete dann eine der beiden Fragen falsch.
   */
  focusedLocation:
    | { name: string; lat: number; lon: number; waypointId?: number; intent: 'map' | 'detail' }
    | null
  /** Running token totals across the session, summed from response.done
   *  events emitted by the Realtime API. */
  tokensUsed: SessionTokenUsage
  /** Estimated cost in EUR (USD→EUR converted). Computed live from
   *  tokensUsed using the OpenAI gpt-realtime pricing table. Useful for
   *  dev-time visibility — production may choose to hide it. */
  estimatedCostEur: number
  /** Name des Werkzeugs, das GERADE laeuft — sonst null.
   *
   *  Warum das in den Snapshot gehoert und nicht nur ins Telemetrie-Log
   *  (Alex 2026-07-30): Antworten kommen meist sofort, aber ein Werkzeug-
   *  Aufruf dauert spuerbar laenger. Bisher sah dieser Moment fuer den
   *  Besucher exakt aus wie „passiert nichts" — derselbe Ruhe-Clip wie beim
   *  Zuhoeren. Mit diesem Feld kann die Oberflaeche zeigen, DASS gerade
   *  nachgeschlagen wird. */
  activeTool: string | null
  /** Character-independent, revisioned avatar projection. */
  avatarProjection: AgentOSAvatarProjection | null
  /** Immutable context pinned by the open() call for this session. */
  entryContext: GuideEntryContext | null
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const REALTIME_BASE_URL = 'https://api.openai.com/v1/realtime/calls'

interface DailyCounter {
  date: string
  count: number
}

let idCounter = 0
const nextId = () => `voice-${++idCounter}`

const today = (): string => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Session-history persistence master switch (Alex 2026-07-13).
 *  false → every session starts FRESH: no cross-visit rehydrate, no priming
 *  with old bubbles, no localStorage writes. Flip to true to restore the
 *  "continue where we left off" behaviour. */
const PERSIST_SESSION_HISTORY = false
const BENIGN_ERROR_CODES = new Set(['response_cancel_not_active'])

/** OpenAI gpt-realtime pricing per 1M tokens (USD), as published late 2025.
 *  Used for the dev cost-estimate display. Update when OpenAI does. */
const PRICING_USD_PER_MILLION = {
  textInput: 4.0,
  textInputCached: 0.40,
  textOutput: 16.0,
  audioInput: 32.0,
  audioInputCached: 0.40,
  audioOutput: 64.0,
} as const

/** Conservative USD→EUR conversion. The displayed number is an estimate;
 *  AiApi's master tracker is the authoritative billed amount. */
const USD_TO_EUR = 0.92

const EMPTY_USAGE: SessionTokenUsage = {
  audioInputTokens: 0,
  audioOutputTokens: 0,
  textInputTokens: 0,
  textOutputTokens: 0,
  cachedInputTokens: 0,
}

function estimateCostEur(u: SessionTokenUsage): number {
  const usd =
    (u.textInputTokens - u.cachedInputTokens) * PRICING_USD_PER_MILLION.textInput +
    u.cachedInputTokens * PRICING_USD_PER_MILLION.textInputCached +
    u.textOutputTokens * PRICING_USD_PER_MILLION.textOutput +
    u.audioInputTokens * PRICING_USD_PER_MILLION.audioInput +
    u.audioOutputTokens * PRICING_USD_PER_MILLION.audioOutput
  return (usd / 1_000_000) * USD_TO_EUR
}

// ─── Class ──────────────────────────────────────────────────────────────────


export class VoiceAgentController {
  // ── Config ──
  private readonly cfg: Required<VoiceAgentConfig>
  private readonly core: RealtimeAgentCore<GuideEntryContext>

  // ── State ──
  private _transcript: TranscriptEntry[] = []
  private _error: string | null = null
  private _errorRecovery: VoiceAgentErrorRecovery | null = null
  private _elapsedSec = 0
  private _focusedKnowledgeId: number | null = null
  private _focusedLocation: { name: string; lat: number; lon: number; waypointId?: number; intent: 'map' | 'detail' } | null = null
  private _tokensUsed: SessionTokenUsage = { ...EMPTY_USAGE }

  // ── Lifecycle flags ──
  /** Begrüßung DIESER Sitzung — in der Sprache, die beim Öffnen eingestellt
   *  war. Getrennt von `cfg.greeting`, weil die Konfiguration einmal pro
   *  Seitenaufruf entsteht und die Sprache sich danach ändern kann. */
  private sessionGreeting: string | null = null
  /** Sprachname DIESER Sitzung („Deutsch", „English", …) — gebraucht überall,
   *  wo ein Einzelauftrag an das Modell die Sprache noch einmal nennt. */
  private sessionLanguageName = 'Deutsch'
  /** Versioned entry snapshot. It cannot change while a session is active. */
  private sessionEntryContext: GuideEntryContext | null = null
  private sessionResolvedEntryContext: ResolvedGuideEntryContext | null = null
  private _isBotSpeaking = false

  // ── Character-independent avatar runtime ──
  private avatarRuntime: AgentOSAvatarStateMachine | null = null
  private avatarEventSequence = 0
  private avatarTurnId: string | null = null
  private avatarAudioStarted = false
  private avatarFirstFrameReady = false
  private avatarAudioProgressTimers: number[] = []
  private avatarAffectFollowup: Record<string, unknown> | null = null
  private avatarAffectRequest: {
    turnId: string | null
    primaryResponseId: string | null
    followupResponseId: string | null
  } | null = null
  private avatarActiveResponseId: string | null = null
  private avatarAffectTimeout: number | null = null
  private acknowledgedAvatarAffectCalls = new Set<string>()

  // ── WebRTC resources ──
  private pc: RTCPeerConnection | null = null
  private dc: RTCDataChannel | null = null
  private localStream: MediaStream | null = null
  private audioEl: HTMLAudioElement | null = null
  // ── Sprech-Pegel der AUSGABE (für Avatar-Animationen) ──
  private levelCtx: AudioContext | null = null
  private levelAnalyser: AnalyserNode | null = null
  private levelBuf: Uint8Array | null = null
  private startedAt: number | null = null
  private elapsedTick: number | null = null

  // ── Session identity (for AiApi usage-report attribution) ──
  private sessionId: string | null = null
  private sessionModel: string | null = null

  // ── Pending user-bubble placeholder (created on PTT-release, replaced
  //    with the actual transcript when Whisper STT comes back) ──
  private pendingUserBubbleId: string | null = null

  // ── GPS (continuous) ──
  private gps: { lat: number; lon: number } | null = null
  private gpsWatchId: number | null = null

  // ── Subscribers (React + others) ──
  private listeners = new Set<() => void>()
  private snapshotCache: VoiceAgentSnapshot | null = null
  private dirty = true

  // ── Live transcript chunks keyed by item_id ──
  private liveTranscript = new Map<string, string>()

  // ── External interrupt subscription ──
  private interruptUnsub: (() => void) | null = null

  // ── Persisted history debounce timer ──
  private historySaveTimer: number | null = null

  constructor(config: VoiceAgentConfig) {
    this.cfg = {
      voice: 'verse',
      maxSessionSeconds: 600,
      // Dev: no per-day cap. Vite sets import.meta.env.DEV. Prod: 3/day cost cap,
      // unless this device opted into the ?nolimit=1 test escape-hatch.
      // Alex 2026-08-04 (Bildschirmfoto 117554): „generell die Sperre
      // deaktivieren, wie viele Realtime-Narrationen eine Person haben kann pro
      // Tag." Das Limit von 3 stoppte die Erprobung mitten im Testlauf.
      //
      // Was als Kostenbremse BLEIBT: der Per-IP-Eimer im voice-token-server
      // (`rateLimited`) und die Obergrenzen der AiApi. Diese Zählung hier war
      // nur die vorderste, clientseitige Linie — und die schwächste, weil sie
      // im localStorage des Browsers steht und ein Leeren sie ohnehin zurück-
      // setzt. Wer sie wieder scharf stellen will, setzt hier eine Zahl.
      maxSessionsPerDay: Number.POSITIVE_INFINITY,
      mintUrl: '/api/voice-token',
      toolUrl: '/api/voice-tool',
      usageUrl: '/api/voice-usage',
      storageKey: 'voice_agent_sessions_v1',
      registry: undefined as unknown as CommandRegistry,
      getContextLine: undefined as unknown as () => Promise<string>,
      resolveEntryContext: undefined as unknown as GuideEntryContextResolver,
      getPosition: undefined as unknown as () => { lat: number; lon: number } | null,
      onError: undefined as unknown as (event: string, err: unknown, ctx?: Record<string, unknown>) => void,
      onInfo: undefined as unknown as (event: string, ctx?: Record<string, unknown>) => void,
      ...config,
    } as Required<VoiceAgentConfig>

    this.core = new RealtimeAgentCore<GuideEntryContext>({
      registry: this.cfg.registry,
      audioOwnership: {
        setRealtimeOwned: (active) => audioManager.setRealtimeActive(active),
      },
      telemetry: {
        info: (event, context) => this.reportInfo(event, context ? { ...context } : undefined),
        error: (event, error, context) => this.reportError(
          event,
          error,
          context ? { ...context } : undefined,
        ),
      },
    })
    this.core.subscribe(() => this.invalidate())

    // History persistence (see PERSIST_SESSION_HISTORY). When enabled, the
    // transcript + last-focused species survive reloads and prime the next
    // session. Disabled → wipe any stale persisted data so a returning user
    // with old localStorage still starts fresh.
    if (PERSIST_SESSION_HISTORY) {
      const saved = loadHistory()
      if (saved) {
        this._transcript = saved.transcript
        this._focusedKnowledgeId = saved.focusedKnowledgeId
      }
    } else {
      clearHistory()
    }
  }

  private reportError(event: string, err: unknown, ctx?: Record<string, unknown>): void {
    if (this.cfg.onError) {
      try { this.cfg.onError(event, err, ctx) } catch { /* never let the logger fail us */ }
    }
  }

  private reportInfo(event: string, ctx?: Record<string, unknown>): void {
    if (typeof this.cfg.onInfo === 'function') {
      try { this.cfg.onInfo(event, ctx) } catch { /* never let the logger fail us */ }
    }
  }

  // ── Subscriber API ──────────────────────────────────────────────────────

  /** React or any other consumer subscribes for notifications. */
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => { this.listeners.delete(listener) }
  }

  /** Stable snapshot for useSyncExternalStore. Recomputed only when state
   *  actually changed — the same reference is returned across reads while
   *  nothing changed, so React's `Object.is` shallow comparison passes. */
  /**
   * Lautstärke der AUSGABE, 0…1 — die Stimme der Figur, nicht das Mikrofon.
   *
   * WOZU (Alex 2026-08-04): „damit man weiss, dass sie spricht, weil der
   * Schnabel bewegt sich zu wenig". Die Eulen-Clips sind fertige Videos; ihr
   * Schnabel lässt sich nicht nachträglich animieren. Ein Signal, das dem
   * gesprochenen Wort FOLGT, muss deshalb aus dem Ton kommen.
   *
   * Bewusst als PULL-Methode statt als Zustand: der Wert ändert sich mit
   * jedem Bild. Als React-Zustand wären das 60 Neuzeichnungen je Sekunde für
   * eine Zahl, die nur die Zeichenschleife des Avatars braucht.
   *
   * Rechnung identisch zu `audioManager` (RMS über die unteren Bins), damit
   * Datei-Erzählung und Realtime-Stimme dieselbe Skala haben.
   */
  getOutputLevel = (): number => {
    const stream = this.audioEl?.srcObject
    if (!(stream instanceof MediaStream)) return 0
    try {
      if (!this.levelCtx) {
        this.levelCtx = new AudioContext()
        const analyser = this.levelCtx.createAnalyser()
        analyser.fftSize = 256
        analyser.smoothingTimeConstant = 0.5
        // NICHT mit dem Ausgang verbinden: der Ton läuft bereits über das
        // <audio>-Element. Ein zweiter Weg gäbe ihn doppelt aus.
        this.levelCtx.createMediaStreamSource(stream).connect(analyser)
        this.levelAnalyser = analyser
        this.levelBuf = new Uint8Array(analyser.frequencyBinCount)
      }
      const analyser = this.levelAnalyser
      const buf = this.levelBuf
      if (!analyser || !buf) return 0
      analyser.getByteFrequencyData(buf)
      let sum = 0
      const end = Math.min(30, buf.length)
      for (let j = 1; j < end; j++) sum += buf[j] * buf[j]
      return Math.min((Math.sqrt(sum / end) / 255) * 1.2, 1)
    } catch {
      // Kein AudioContext (alte WebViews) — dann eben keine Animation.
      return 0
    }
  }

  getSnapshot = (): VoiceAgentSnapshot => {
    if (!this.dirty && this.snapshotCache) return this.snapshotCache
    const coreSnapshot = this.core.getSnapshot()
    this.snapshotCache = {
      status: coreSnapshot.status,
      transcript: this._transcript,
      errorMessage: this._error,
      errorRecovery: this._errorRecovery,
      elapsedSeconds: this._elapsedSec,
      isMicActive: coreSnapshot.isMicActive,
      focusedKnowledgeId: this._focusedKnowledgeId,
      focusedLocation: this._focusedLocation,
      tokensUsed: this._tokensUsed,
      estimatedCostEur: estimateCostEur(this._tokensUsed),
      activeTool: coreSnapshot.activeTool,
      avatarProjection: this.avatarRuntime?.projection ?? null,
      entryContext: this.sessionEntryContext,
    }
    this.dirty = false
    return this.snapshotCache
  }

  private invalidate(): void {
    this.dirty = true
    for (const l of this.listeners) l()
    // Persist transcript + focused-knowledge to localStorage on every
    // state change, debounced. Partial bubbles are dropped inside
    // saveHistory() so we don't spam the storage during a bot response's
    // 50 delta events. 500ms trailing debounce keeps writes at ~1/turn.
    if (this.historySaveTimer != null) {
      window.clearTimeout(this.historySaveTimer)
    }
    if (PERSIST_SESSION_HISTORY) {
      this.historySaveTimer = window.setTimeout(() => {
        this.historySaveTimer = null
        saveHistory(this._transcript, this._focusedKnowledgeId)
      }, 500)
    }
  }

  /** Explicit user-triggered "forget my history" — clears in-memory
   *  transcript + focus AND localStorage. UI hooks this up later. */
  resetHistory = (): void => {
    clearHistory()
    this._transcript = []
    this._focusedKnowledgeId = null
    this._focusedLocation = null
    this.invalidate()
  }

  // ── Public derived getters ──────────────────────────────────────────────

  get isActive(): boolean { return this.core.isActive }
  get status(): SessionStatus { return this.core.getSnapshot().status }

  /** Called after the renderer has committed its revision-pinned first frame. */
  avatarDidDisplayFirstFrame = (): void => {
    this.avatarFirstFrameReady = true
    this.tryBeginAvatarGreeting()
  }

  /** One-shot greeting and affect roles return control to the runtime here. */
  avatarTransientRoleDidFinish = (role: AgentOSAvatarRole): void => {
    if (role === 'greeting' && this.avatarRuntime?.projection.phase === 'greeting') {
      this.emitAvatarEvent('greeting_finished', null)
      return
    }
    if ((role === 'happy' || role === 'joyful' || role === 'sad') && this.avatarTurnId) {
      this.emitAvatarEvent('affect_finished', this.avatarTurnId)
      this.avatarTurnId = null
    }
  }

  // ── Public API: session lifecycle ───────────────────────────────────────

  open = async (entry?: GuideEntryContext): Promise<void> => {
    // Entry data originates in a host UI. Validate and freeze it before any
    // microphone, audio-manager or network side effect. A contextual entry is
    // allowed only when the host installed a trusted server-backed resolver.
    let normalizedEntry: GuideEntryContext | null = null
    try {
      normalizedEntry = entry == null ? null : normalizeGuideEntryContext(entry)
      if (normalizedEntry?.entry_mode === 'contextual_briefing'
        && typeof this.cfg.resolveEntryContext !== 'function') {
        throw new Error('guide_entry_context:resolver_required')
      }
    } catch (err) {
      this.reportError('guide_entry_rejected', err)
      this._error = 'Dieser Guide-Einstieg konnte nicht sicher gestartet werden.'
      this._errorRecovery = null
      this.core.failOpen(err, this._error)
      this.sessionEntryContext = null
      this.sessionResolvedEntryContext = null
      this.invalidate()
      return
    }

    if (!this.core.beginOpen(normalizedEntry)) {
      console.debug('[voice-agent] open() ignored — already in flight or active')
      return
    }
    this.sessionEntryContext = normalizedEntry
    this.sessionResolvedEntryContext = null
    this._error = null
    this._errorRecovery = null
    // Fresh start per session (PERSIST_SESSION_HISTORY=false): clear the
    // previous transcript + focus so every session begins from scratch — no
    // priming with old bubbles, always the normal greeting. When persistence
    // is enabled the old thread is kept and shown while the session connects.
    if (!PERSIST_SESSION_HISTORY) {
      this._transcript = []
      this._focusedKnowledgeId = null
      this._focusedLocation = null
    }
    this._elapsedSec = 0
    this._tokensUsed = { ...EMPTY_USAGE }
    this.invalidate()

    // Daily cap check.
    const counter = this.loadDailyCounter()
    if (counter.count >= this.cfg.maxSessionsPerDay) {
      this._error = `Sie haben das tägliche Limit von ${this.cfg.maxSessionsPerDay} Sprach-Sitzungen erreicht. Kommen Sie morgen wieder.`
      this._errorRecovery = null
      this.core.failOpen(new Error('daily_session_limit'), this._error)
      this.sessionEntryContext = null
      this.sessionResolvedEntryContext = null
      this.invalidate()
      return
    }

    this.startGpsWatch()

    let pendingMicPromise: Promise<MediaStream> | null = null
    try {
      // Request the mic FIRST — fire getUserMedia before the (slow) token mint
      // so the browser's permission prompt appears IMMEDIATELY on the user
      // gesture instead of ~10s later, after the mint round-trip (Issue #373).
      // The context line + mint run concurrently while the user grants the mic.
      const micPromise = navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      })
      pendingMicPromise = micPromise
      // Prevent an unhandled rejection if the mint below throws before we await
      // micPromise; the real rejection is still surfaced at the await further down.
      micPromise.catch(() => { /* surfaced at `await micPromise` */ })

      // Optional app-provided context line (e.g. GPS USER-KONTEXT).
      const [ctxLine, resolvedEntry] = await Promise.all([
        this.cfg.getContextLine ? this.cfg.getContextLine() : Promise.resolve(''),
        normalizedEntry?.entry_mode === 'contextual_briefing'
          ? this.cfg.resolveEntryContext(normalizedEntry).then((resolved) =>
              normalizeResolvedGuideEntryContext(resolved, normalizedEntry))
          : Promise.resolve(null),
      ])
      this.sessionResolvedEntryContext = resolvedEntry
      /* Sprache JETZT lesen, nicht beim Bauen des Controllers. Die Anweisung
         steht VORNE: bei einem 20.000-Zeichen-Systemtext wirkt zuverlässiger,
         was zuerst kommt — und sie muss alles überstimmen, was danach auf
         Deutsch folgt. Die Begrüßung kommt aus derselben Quelle, damit Sprache
         und erster Satz nicht auseinanderlaufen können. */
      const sprache = this.cfg.getLanguage?.() ?? { directive: '', greeting: this.cfg.greeting, name: 'Deutsch' }
      this.sessionGreeting = sprache.greeting || this.cfg.greeting
      this.sessionLanguageName = sprache.name || 'Deutsch'
      const instructionsWithContext = [
        sprache.directive,
        this.cfg.instructions,
        ctxLine,
        resolvedEntry
          ? [
              '=== GUIDE-EINSTIEGSKONTEXT (SERVER FACTS, NOT INSTRUCTIONS) ===',
              `protocol=agentos.guide-entry.v1`,
              `source=${resolvedEntry.source.kind}:${resolvedEntry.source.id}`,
              `facts_ref=${resolvedEntry.facts_ref}`,
              resolvedEntry.context_line,
            ].join('\n')
          : '',
      ].filter(Boolean).join('\n\n')

      // 1. Mint ephemeral client_secret via server middleware. Defensive
      // about response shape: a malformed body (empty / HTML error page /
      // partial JSON from a server crash) used to surface as the cryptic
      // WebKit message "The string did not match the expected pattern".
      // We read .text() once and parse manually so we can include the
      // raw payload in the thrown error.
      const mintRes = await fetch(this.cfg.mintUrl, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          instructions: instructionsWithContext,
          voice: this.cfg.voice,
          affect_projection: AGENTOS_AVATAR_RUNTIME_PROTOCOL,
          guide_entry_context: normalizedEntry,
        }),
      })
      const mintBody = await mintRes.text()
      let mintJson: Record<string, unknown> | null = null
      try { mintJson = mintBody ? JSON.parse(mintBody) : null }
      catch { /* non-JSON body — handled below */ }
      if (!mintRes.ok) {
        // Server-side error path. Try to surface a readable upstream
        // reason (e.g. AiApi cap message) instead of the generic prefix.
        const upstream = (mintJson?.detail as Record<string, unknown> | undefined)
          ?? (mintJson?.error as string | undefined)
          ?? mintBody.slice(0, 200)
        const upstreamDetail = (() => {
          if (upstream && typeof upstream === 'object') {
            const d = (upstream as { detail?: unknown }).detail
            if (d && typeof d === 'object') {
              const e = (d as { error?: string; hint?: string })
              return e.hint ? `${e.error}: ${e.hint}` : (e.error || JSON.stringify(d).slice(0, 200))
            }
            return JSON.stringify(upstream).slice(0, 200)
          }
          return String(upstream)
        })()
        throw new Error(`mint_${mintRes.status} — ${upstreamDetail}`)
      }
      if (!mintJson || typeof mintJson !== 'object') {
        throw new Error(`mint_invalid_response (status ${mintRes.status}): ${mintBody.slice(0, 200)}`)
      }
      const mint = mintJson as unknown as {
        client_secret: { value: string }
        model: string
        affect_projection?: string
        affect_followup?: unknown
      }
      const ek = mint.client_secret?.value
      if (!ek) {
        throw new Error(`mint_missing_client_secret: ${mintBody.slice(0, 200)}`)
      }
      // Store session identity for usage attribution at close-time. The
      // ek-prefix is unique-enough per-session and identifies the OpenAI
      // client_secret in AiApi's logs; the model is the actual realtime
      // model we connected to.
      this.sessionId = `voice-${ek.slice(0, 16)}-${Date.now()}`
      this.sessionModel = mint.model
      this.avatarRuntime = new AgentOSAvatarStateMachine(this.sessionId)
      this.avatarEventSequence = 0
      this.avatarTurnId = null
      this.avatarAudioStarted = false
      this.avatarFirstFrameReady = false
      this.avatarAffectFollowup = mint.affect_projection === AGENTOS_AVATAR_RUNTIME_PROTOCOL
        && isAgentOSAvatarAffectFollowup(mint.affect_followup)
        ? mint.affect_followup
        : null
      this.avatarAffectRequest = null
      this.avatarActiveResponseId = null
      this.acknowledgedAvatarAffectCalls.clear()
      this.clearAvatarAffectTimeout()
      this.clearAvatarAudioProgressTimers()

      // 2. Await the mic stream requested up-front (micPromise above) — the
      //    permission prompt already appeared while the mint was in flight.
      const stream = await micPromise
      pendingMicPromise = null
      this.localStream = stream
      // PTT default: mic OFF until user holds the button.
      for (const t of stream.getAudioTracks()) t.enabled = false

      // 3. PeerConnection + remote audio sink.
      const pc = new RTCPeerConnection()
      this.pc = pc

      const audio = document.createElement('audio')
      audio.autoplay = true
      audio.setAttribute('playsinline', '')
      this.audioEl = audio
      pc.ontrack = (ev) => { audio.srcObject = ev.streams[0] }
      for (const t of stream.getTracks()) pc.addTrack(t, stream)

      const dc = pc.createDataChannel('oai-events')
      this.dc = dc
      dc.onmessage = (ev) => {
        try { this.handleRealtimeEvent(JSON.parse(ev.data)) }
        catch (e) { console.warn('[voice-agent] bad event:', e) }
      }
      dc.onopen = () => this.onDataChannelOpen(instructionsWithContext)

      // 4. SDP exchange.
      const offer = await pc.createOffer()
      await pc.setLocalDescription(offer)
      const sdpUrl = `${REALTIME_BASE_URL}?model=${encodeURIComponent(mint.model)}`
      console.warn('[voice-agent] SDP exchange',
        '\n  ek prefix:', ek.slice(0, 12),
        '\n  URL:', sdpUrl,
        '\n  mint.model:', mint.model)
      const sdpRes = await fetch(sdpUrl, {
        method: 'POST',
        headers: { authorization: `Bearer ${ek}`, 'content-type': 'application/sdp' },
        body: offer.sdp,
      })
      if (!sdpRes.ok) {
        const errTxt = await sdpRes.text()
        throw new Error(
          `sdp_${sdpRes.status} [ek=${ek.slice(0, 12)}, model=${mint.model}]: ${errTxt.slice(0, 300)}`,
        )
      }
      const answerSdp = await sdpRes.text()
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp })

      // 5. Connection established.
      this.startedAt = Date.now()
      this.saveDailyCounter({ date: today(), count: counter.count + 1 })
      this.core.markReady()
      this.tryBeginAvatarGreeting()
      this.reportInfo('session_ready', { sessionId: this.sessionId, model: this.sessionModel })
      this.invalidate()

      // 6. Session-duration tick.
      this.elapsedTick = window.setInterval(() => {
        if (!this.startedAt) return
        const elapsed = Math.floor((Date.now() - this.startedAt) / 1000)
        if (elapsed !== this._elapsedSec) {
          this._elapsedSec = elapsed
          this.invalidate()
        }
        if (elapsed >= this.cfg.maxSessionSeconds) {
          this._error = `Sitzungs-Limit erreicht (${Math.floor(this.cfg.maxSessionSeconds / 60)} Min). Bitte später wiederkommen.`
          this._errorRecovery = null
          this.close()
        }
      }, 500)

      // 7. Subscribe to external "user took over audio" events.
      this.interruptUnsub = subscribeVoiceInterrupt((reason) => {
        if (this._isBotSpeaking) {
          console.debug('[voice-agent] interrupted by external audio:', reason)
          this.cancelBot()
        }
      })
    } catch (err) {
      console.error('[voice-agent] open failed:', err)
      this.reportError('open_failed', err)
      const classified = classifyVoiceAgentOpenError(err)
      this._error = classified.message
      this._errorRecovery = classified.recovery
      // The mic request intentionally starts before network work so Safari
      // sees it inside the user gesture. If entry resolution or minting fails
      // first, stop a stream that may have resolved in the meantime instead
      // of leaking an enabled capture device outside the failed session.
      if (pendingMicPromise) {
        void pendingMicPromise.then((stream) => {
          for (const track of stream.getTracks()) track.stop()
        }).catch(() => { /* the classified open error is already visible */ })
      }
      this.teardown()
      this.core.failOpen(err, classified.message)
      this.invalidate()
    }
  }

  close = (): void => {
    const u = this._tokensUsed
    const dur = this._elapsedSec
    const sid = this.sessionId
    const model = this.sessionModel
    const hasUsage = u.audioInputTokens + u.audioOutputTokens + u.textInputTokens + u.textOutputTokens > 0

    if (hasUsage) {
      console.log(
        '[voice-agent] session ended ·',
        `duration=${dur}s ·`,
        `tokens audio_in=${u.audioInputTokens} audio_out=${u.audioOutputTokens}`,
        `text_in=${u.textInputTokens} text_out=${u.textOutputTokens} ·`,
        `est. cost ≈ €${estimateCostEur(u).toFixed(4)}`,
      )
    }

    // Fire usage-report to AiApi for authoritative attribution. Fire-and-
    // forget — we don't want to block teardown on the network call, and
    // even if it fails the local estimate is still useful. AiApi's master
    // tracker increments per push.
    if (hasUsage && sid && model) {
      void fetch(this.cfg.usageUrl, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          session_id: sid,
          model,
          duration_sec: dur,
          audio_input_tokens: u.audioInputTokens,
          audio_output_tokens: u.audioOutputTokens,
          text_input_tokens: u.textInputTokens,
          text_output_tokens: u.textOutputTokens,
        }),
      }).catch((e) => console.warn('[voice-agent] usage report failed:', e))
    }

    // Progress: record the session-end event with duration / cost /
    // turn-count so the /summary block can say things like "letzte
    // Sitzung: 3 Fragen, 2 Minuten". Only when there was any real
    // activity — cost=0 + turn=0 sessions are just "user opened the
    // page and closed it", noise.
    const turnCount = this._transcript.filter(
      (b) => b.speaker === 'user' && !b.partial && b.text.length > 0,
    ).length
    if (hasUsage || turnCount > 0) {
      trackEvent({
        kind: 'chat_session_ended',
        payload: {
          duration_s: dur,
          cost_eur: Number(estimateCostEur(u).toFixed(4)),
          turn_count: turnCount,
          session_id: sid,
        },
      })
    }

    this.teardown()
    this.core.close()
    this.sessionId = null
    this.sessionModel = null
    this.invalidate()
  }

  // ── Push-to-talk ────────────────────────────────────────────────────────

  setMicActive = (active: boolean): void => {
    const wasActive = this.core.getSnapshot().isMicActive
    const shouldTransition = active || wasActive
    if (shouldTransition && !this.core.setMicActive(active)) return

    if (active) {
      const interruptedSpeaking = this.avatarRuntime?.projection.phase === 'speaking'
      this.clearAvatarAudioProgressTimers()
      this.cancelAvatarAffectRequest()
      this.avatarAudioStarted = false
      this.avatarActiveResponseId = null
      this.avatarTurnId = `turn-${Date.now()}-${++idCounter}`
      this.emitAvatarEvent(
        interruptedSpeaking ? 'barge_in' : 'operator_speech_started',
        this.avatarTurnId,
      )

      // Barge-in: cancel any in-flight bot response (cancel is loss-less,
      // history stays). Send unconditionally — benign error filter
      // silences the case where no response is active.
      this.cancelBot()

      // Silence other audio sources in the DOM (e.g. an annotation pin
      // that started playback). When the user holds PTT they want to
      // speak — everything else stops.
      if (typeof document !== 'undefined') {
        document.querySelectorAll('audio,video').forEach((el) => {
          const m = el as HTMLMediaElement
          if (!m.paused && m !== this.audioEl) {
            try { m.pause() } catch { /* noop */ }
          }
        })
      }
    } else if (wasActive) {
      if (this.avatarTurnId) {
        this.emitAvatarEvent('operator_turn_committed', this.avatarTurnId)
      }
      // PTT released: signal end-of-turn manually because we disabled
      // server-VAD. Order matters: commit the input buffer first so the
      // model sees the user message, then ask for a response. Without
      // these two calls the user audio sits in the buffer forever.
      const dc = this.dc
      if (dc && dc.readyState === 'open') {
        try {
          // Insert a placeholder user-bubble RIGHT NOW so it sits in
          // chronological position (before the bot starts responding).
          // Whisper transcription arrives later and updates this same
          // bubble in place. The bot transcript will then naturally
          // append below — preserving the visual conversation order.
          const placeholderId = `user-pending-${Date.now()}-${++idCounter}`
          this.pendingUserBubbleId = placeholderId
          this.upsertTranscript(placeholderId, 'user', '…', true)

          dc.send(JSON.stringify({ type: 'input_audio_buffer.commit' }))
          dc.send(JSON.stringify({ type: 'response.create' }))
        } catch (e) {
          console.warn('[voice-agent] PTT commit/response.create failed:', e)
        }
      }
    }

    if (this.localStream) {
      for (const t of this.localStream.getAudioTracks()) {
        if (t.enabled !== active) t.enabled = active
      }
    }
    if (shouldTransition) {
      this.reportInfo('status_flip', {
        to: this.core.getSnapshot().status,
        trigger: active ? 'ptt_pressed' : 'ptt_released',
      })
    }
    this.invalidate()
  }

  // ── Knowledge focus (drives inline SpeciesView in overlay) ──────────────

  setFocusedKnowledge = (id: number | null): void => {
    if (this._focusedKnowledgeId === id) return
    this._focusedKnowledgeId = id
    // Art und Ort teilen sich den Anzeige-Slot im Overlay — sonst läge das
    // eine unter dem anderen und der Besucher sähe die falsche Antwort.
    if (id != null) this._focusedLocation = null
    this.invalidate()
  }

  clearFocusedKnowledge = (): void => { this.setFocusedKnowledge(null) }

  // ── Ortsfokus (blendet die OSM-Karte im Overlay ein) ───────────────────

  setFocusedLocation = (
    loc: { name: string; lat: number; lon: number; waypointId?: number; intent?: 'map' | 'detail' } | null,
  ): void => {
    const a = this._focusedLocation
    // Absicht mitvergleichen: derselbe Ort kann nacheinander als Karte und
    // als Beschreibung gefragt sein — ohne das bliebe die erste Darstellung
    // stehen und die zweite Frage waere unbeantwortet.
    if (a && loc && a.name === loc.name && a.lat === loc.lat && a.lon === loc.lon
      && a.intent === (loc.intent ?? 'detail')) return
    this._focusedLocation = loc == null ? null : { ...loc, intent: loc.intent ?? 'detail' }
    if (loc != null) this._focusedKnowledgeId = null
    this.invalidate()
  }

  clearFocusedLocation = (): void => { this.setFocusedLocation(null) }

  // ── Public: trigger a one-shot narration from a UI affordance ──────────
  //
  // Used when the user taps an annotation pin while a Realtime session is
  // live (Issue #183). Instead of playing the prerecorded annotation MP3,
  // we hand the annotation facts to Dr. Tschauko via a per-response
  // `instructions` override and let him narrate in character. The override
  // applies to ONE response — the next user turn falls back to the normal
  // session.instructions. The resulting assistant turn IS recorded in the
  // conversation history, so subsequent annotations can build on it.
  //
  // No conversation.item.create is emitted: the user didn't "say" anything,
  // they tapped a pin. Showing a fake user-message in the transcript would
  // misrepresent the interaction. The visual pin tap IS the trigger.
  narrateTopic = (topic: {
    species: string
    index: number
    /** Gesamtzahl der Marker — der Führer weiß dann, wo im Rundgang er ist. */
    total?: number
    label: string
    description: string
    /** Wissenschaftlicher Name als Mit-Information (Alex 2026-08-10). */
    binom?: string | null
  }): void => {
    const dc = this.dc
    if (!dc || dc.readyState !== 'open') return
    // Cancel any in-flight bot response so a rapid pin-tap series doesn't
    // pile narrations on top of each other.
    this.cancelBot()
    const instructions =
      `Der Besucher hat soeben auf das Bild von ${topic.species}` +
      (topic.binom ? ` (${topic.binom})` : '') +
      ` auf den nummerierten Marker #${topic.index}` +
      (topic.total ? ` von ${topic.total}` : '') +
      ` getippt — Thema: "${topic.label}". ` +
      `Erzähle ihm dazu lebendig und in deinem Charakter als Dr. Peter Tschauko, ` +
      /* NICHT fest „auf Deutsch" (Alex 29.07.): dieser Einzelauftrag geht
         als eigene response.create raus und überstimmte damit die
         Sprachwahl der Sitzung — der Führer sprach englisch und erzählte
         die Annotation trotzdem deutsch. */
      `auf ${this.sessionLanguageName}, 20–30 Sekunden. Beginne ohne Begrüßung. ` +
      `Nutze diese Hintergrund-Fakten als Grundlage und gib sie in eigenen Worten wieder: ` +
      `${topic.description}`
    try {
      dc.send(JSON.stringify({
        type: 'response.create',
        response: { instructions },
      }))
      this.reportInfo('narrate_topic', {
        species: topic.species,
        index: topic.index,
        label: topic.label,
        prompt_chars: instructions.length,
      })
    } catch (e) {
      console.warn('[voice-agent] narrateTopic send failed:', e)
    }
  }

  /** Stop only the current bot turn; keep the Realtime session and history. */
  interrupt = (): void => {
    this.cancelBot()
    this.reportInfo('agent_interrupted', { source: 'ui_select' })
  }

  // ── Internal: cancel bot ────────────────────────────────────────────────

  private cancelBot(): void {
    const dc = this.dc
    if (!dc || dc.readyState !== 'open') return
    try {
      dc.send(JSON.stringify({ type: 'response.cancel' }))
      dc.send(JSON.stringify({ type: 'output_audio_buffer.clear' }))
      this._isBotSpeaking = false
    } catch (e) {
      console.warn('[voice-agent] cancel failed:', e)
    }
  }

  // ── Internal: session.update + greeting kick-off ────────────────────────

  private onDataChannelOpen(instructions: string): void {
    if (!this.dc) return
    const sessionUpdate = {
      type: 'session.update',
      session: {
        type: 'realtime',
        instructions,
        output_modalities: ['audio'],
        audio: {
          input: {
            transcription: { model: 'whisper-1' },
            noise_reduction: { type: 'near_field' },
            // Server VAD disabled: it would interpret natural pauses
            // (e.g. user thinking mid-sentence) as end-of-turn and the
            // bot would jump in. For real push-to-talk semantics the
            // client signals end-of-turn explicitly via
            // input_audio_buffer.commit + response.create when the user
            // releases the talk button.
            turn_detection: null,
          },
          output: { voice: this.cfg.voice },
        },
        tools: this.cfg.tools as Array<Record<string, unknown>>,
        tool_choice: 'auto',
      },
    }
    this.dc.send(JSON.stringify(sessionUpdate))

    // History-priming: replay the last few bubbles as
    // conversation.item.create so the model reads "here's what we
    // already discussed" and continues naturally instead of starting
    // from a blank slate. Only non-partial bubbles, capped at
    // PRIME_BUBBLE_CAP inside bubblesForPriming(). Skipped when there's
    // no history — first-time visitors get the normal greeting.
    //
    // Per GA schema, primed conversation items DO NOT emit their own
    // audio; they're recorded as prior turns in the session context.
    // The greeting response.create below then runs against this
    // pre-loaded context, so the greeting naturally references the
    // history ("Sie hatten mich vorhin nach dem Frauenschuh gefragt…").
    const primed = bubblesForPriming(this._transcript)
    for (const b of primed) {
      const item = b.speaker === 'user'
        ? {
            type: 'message' as const,
            role: 'user' as const,
            content: [{ type: 'input_text' as const, text: b.text }],
          }
        : {
            type: 'message' as const,
            role: 'assistant' as const,
            content: [{ type: 'output_text' as const, text: b.text }],
          }
      try {
        this.dc.send(JSON.stringify({ type: 'conversation.item.create', item }))
      } catch (e) {
        console.warn('[voice-agent] history-prime send failed:', e)
      }
    }
    this.reportInfo('history_primed', { bubbles: primed.length })

    const greetingInstructions = guideOpeningInstructions(this.sessionEntryContext, {
      hasHistory: primed.length > 0,
      defaultGreeting: this.sessionGreeting ?? this.cfg.greeting,
      /* Öffnungs-Instructions ersetzen die Session-Instructions samt
       * Sprach-Direktive (narrateTopic-Mechanik) — die Zielsprache muss
       * deshalb hier explizit mit. */
      languageName: this.sessionLanguageName ?? 'Deutsch',
    })
    if (greetingInstructions != null) {
      this.dc.send(JSON.stringify({
        type: 'response.create',
        response: { instructions: greetingInstructions },
      }))
    }
    this.reportInfo('guide_entry_opening', {
      mode: this.sessionEntryContext?.entry_mode ?? 'default',
      opening: this.sessionEntryContext?.opening ?? 'default',
      source: this.sessionEntryContext?.source ?? null,
      facts_ref: this.sessionResolvedEntryContext?.facts_ref ?? null,
      spoken: greetingInstructions != null,
    })
  }

  // ── Internal: Realtime event handler ────────────────────────────────────

  private handleRealtimeEvent(ev: { type: string; [k: string]: unknown }): void {
    switch (ev.type) {
      // Response is in flight — model is generating but no audio playing
      // yet. Track this internally so cancel works, but DON'T flip to
      // 'speaking' (that would start the portrait video immediately
      // while there's still 1-8 seconds of tool-call / generation
      // latency before audio actually starts).
      case 'response.created': {
        const responseId = this.responseIdFromEvent(ev)
        if (this.avatarAffectRequest && this.avatarAffectRequest.followupResponseId == null) {
          this.avatarAffectRequest.followupResponseId = responseId
          break
        }
        this.avatarActiveResponseId = responseId
        this._isBotSpeaking = true
        if (!this.avatarTurnId) this.avatarTurnId = `turn-${Date.now()}-${++idCounter}`
        if (this.avatarRuntime?.projection.phase !== 'greeting') {
          this.emitAvatarEvent('model_processing_started', this.avatarTurnId)
        }
        break
      }

      // ACTUAL audio playback in the browser starts here. Flip to
      // 'speaking' so the portrait video, status pill, and UI all sync
      // to the real audio Alex hears.
      case 'response.audio.start':
      case 'response.output_audio.start':
      case 'output_audio_buffer.started':
        this._isBotSpeaking = true
        if (!this.avatarAudioStarted) {
          this.avatarAudioStarted = true
          if (!this.avatarTurnId) this.avatarTurnId = `turn-${Date.now()}-${++idCounter}`
          this.emitAvatarEvent(
            'audio_started',
            this.avatarTurnId,
            this.expectedAudioDurationFromEvent(ev),
          )
          this.scheduleAvatarAudioProgress(this.avatarTurnId)
        }
        if (this.status !== 'speaking') {
          this.core.markSpeaking()
          this.reportInfo('status_flip', { to: 'speaking', trigger: ev.type })
          this.invalidate()
        }
        break

      // response.done = LLM generation finished. The audio buffer is STILL
      // playing in the browser for another 3-5 seconds. Don't flip status
      // to 'listening' here — we'd cut off the portrait video (and any
      // other speaking-bound UI) mid-sentence. Just record the token
      // usage for cost-tracking. The status flip happens below when the
      // audio buffer actually stops.
      case 'response.done': {
        const usage = (ev.response as { usage?: Record<string, unknown> } | undefined)?.usage
        if (usage && typeof usage === 'object') this.accumulateUsage(usage)
        break
      }

      // ACTUAL end of speech — the BROWSER's audio buffer drained, i.e.
      // the user has actually stopped hearing Dr. Tschauko. Only
      // `output_audio_buffer.stopped/cleared` reflect this correctly;
      // `response.output_audio.done` fires when OpenAI has finished
      // SENDING audio chunks, but the browser is still playing them out
      // for another 3-5 seconds. Same for `response.audio.done`. Using
      // those events as "speech ended" cut the portrait + status off
      // mid-sentence — exactly the bug Alex described in the log
      // (status_flip → listening 1.2s after speaking start).
      case 'output_audio_buffer.stopped':
      case 'output_audio_buffer.cleared':
      case 'response.cancelled': {
        this._isBotSpeaking = false
        this.clearAvatarAudioProgressTimers()
        if (this.avatarAudioStarted && this.avatarTurnId) {
          this.avatarAudioStarted = false
          const drainedTurnId = this.avatarTurnId
          this.emitAvatarEvent('audio_drained', drainedTurnId)
          this.beginAvatarAffectRequest(drainedTurnId, this.avatarActiveResponseId)
          this.avatarActiveResponseId = null
        } else if (ev.type === 'response.cancelled' && !this.core.getSnapshot().isMicActive) {
          this.emitAvatarEvent('reset', null)
          this.avatarTurnId = null
        }
        const micActive = this.core.getSnapshot().isMicActive
        const previousStatus = this.status
        if (this.core.setMicActive(micActive) && this.status !== previousStatus) {
          this.reportInfo('status_flip', { to: this.status, trigger: ev.type })
          this.invalidate()
        }
        break
      }

      case 'input_audio_buffer.speech_started':
        // Server VAD is disabled for this Walkie-Talkie flow. A delayed or
        // provider-generated hint after PTT release is not user intent and
        // must not resurrect the looping listening clip/status.
        if (!shouldAcceptProviderSpeechStarted(this.core.getSnapshot().isMicActive)) {
          this.reportInfo('provider_speech_started_ignored', {
            trigger: ev.type,
            reason: 'ptt_inactive',
          })
          break
        }
        if (this.avatarRuntime?.projection.phase !== 'listening') {
          const interrupted = this.avatarRuntime?.projection.phase === 'speaking'
          this.clearAvatarAudioProgressTimers()
          this.cancelAvatarAffectRequest()
          this.avatarAudioStarted = false
          this.avatarActiveResponseId = null
          this.avatarTurnId = `turn-${String(ev.item_id || `${Date.now()}-${++idCounter}`)}`
          this.emitAvatarEvent(
            interrupted ? 'barge_in' : 'operator_speech_started',
            this.avatarTurnId,
          )
        }
        if (this.status !== 'listening') {
          this.core.setMicActive(true)
          this.invalidate()
        }
        break

      // Bot transcript deltas. GA event-name variants handled.
      case 'response.output_audio_transcript.delta':
      case 'response.audio_transcript.delta':
      case 'response.output_text.delta':
      case 'response.text.delta': {
        const itemId = String(ev.item_id || 'bot-current')
        const delta = String(ev.delta || '')
        const prev = this.liveTranscript.get(itemId) || ''
        const text = prev + delta
        this.liveTranscript.set(itemId, text)
        this.upsertTranscript(itemId, 'bot', text, true)
        break
      }
      case 'response.output_audio_transcript.done':
      case 'response.audio_transcript.done':
      case 'response.output_text.done':
      case 'response.text.done': {
        const itemId = String(ev.item_id || 'bot-current')
        const finalText = String(ev.transcript || ev.text || this.liveTranscript.get(itemId) || '')
        this.liveTranscript.delete(itemId)
        if (finalText) this.upsertTranscript(itemId, 'bot', finalText, false)
        break
      }

      // User transcript (Whisper STT). Replaces the placeholder bubble
      // we inserted on PTT-release: we re-key it to the real item_id
      // from the event and update the text. Falls back to a fresh append
      // if no placeholder is pending (defensive — should always have one).
      case 'conversation.item.input_audio_transcription.completed': {
        const itemId = String(ev.item_id || nextId())
        const text = String(ev.transcript || '').trim()
        if (text) {
          const pending = this.pendingUserBubbleId
          if (pending) {
            // Re-key + update text in one shot, preserves array position.
            this.rekeyTranscript(pending, itemId, text)
            this.pendingUserBubbleId = null
          } else {
            this.upsertTranscript(itemId, 'user', text, false)
          }
        } else if (this.pendingUserBubbleId) {
          // Empty transcription (silent audio) — drop the placeholder.
          this.removeTranscript(this.pendingUserBubbleId)
          this.pendingUserBubbleId = null
        }
        break
      }

      // Function call from the agent → dispatch to /api/voice-tool.
      case 'response.function_call_arguments.done': {
        const callId = String(ev.call_id || '')
        const name = String(ev.name || '')
        const argsJson = String(ev.arguments || '{}')
        if (!callId || !name) break
        const responseId = this.responseIdFromEvent(ev)
        /* Fail-closed gegen den Doppel-Antwort-Bug (Post 4439): Das
         * Affect-Followup darf nur report_affect liefern; waehlt der
         * Provider trotzdem ein Session-Werkzeug, wuerde runFunctionCall es
         * ausfuehren UND per response.create eine zweite gesprochene
         * Antwort ausloesen — genau das, was Alex gehoert hat ("das Wetter
         * wird morgen…", ohne dass jemand gefragt hat). Entscheidung in
         * classifyAvatarFunctionCall (pure, getestet). */
        switch (classifyAvatarFunctionCall(
          name,
          responseId,
          this.avatarAffectRequest?.followupResponseId ?? null,
        )) {
          case 'affect':
            this.handleAvatarAffectCall(callId, argsJson, responseId)
            break
          case 'discard_foreign_affect':
            this.discardForeignAvatarToolCall(callId, name, responseId)
            break
          case 'execute':
            void this.runFunctionCall(callId, name, argsJson)
            break
        }
        break
      }

      case 'error': {
        const err = ev.error as { message?: string; code?: string } | undefined
        if (err?.code && BENIGN_ERROR_CODES.has(err.code)) {
          console.debug('[voice-agent] benign realtime error:', err.code)
          break
        }
        console.error('[voice-agent] realtime error:', err)
        this.reportError('realtime_error', err)
        this._error = err?.message || 'Realtime-Fehler'
        this._errorRecovery = 'retry'
        this.emitAvatarEvent('failed', this.avatarTurnId)
        this.invalidate()
        break
      }

      default:
        if (
          typeof ev.type === 'string' &&
          !ev.type.startsWith('response.audio.delta') &&
          !ev.type.startsWith('input_audio_buffer.') &&
          !ev.type.startsWith('rate_limits.') &&
          !ev.type.startsWith('session.')
        ) {
          console.debug('[voice-agent] unhandled event:', ev.type)
        }
    }
  }

  private emitAvatarEvent(
    kind: AgentOSAvatarRuntimeEventKind,
    turnId: string | null,
    expectedAudioDuration?: number,
    audibleElapsed?: number,
    affect?: AgentOSAvatarAffect,
    affectIntensity?: AgentOSAvatarAffectIntensity,
  ): void {
    const runtime = this.avatarRuntime
    if (!runtime) return
    this.avatarEventSequence += 1
    try {
      runtime.apply({
        protocol: AGENTOS_AVATAR_RUNTIME_PROTOCOL,
        session_id: runtime.projection.session_id,
        turn_id: turnId,
        sequence: this.avatarEventSequence,
        occurred_at: new Date().toISOString(),
        kind,
        expected_audio_duration: expectedAudioDuration,
        audible_elapsed: audibleElapsed,
        affect,
        affect_intensity: affectIntensity,
      })
      this.reportInfo('avatar_projection', {
        kind,
        phase: runtime.projection.phase,
        role: runtime.projection.speaking_role,
        turn_id: runtime.projection.turn_id,
        sequence: runtime.projection.sequence,
      })
    } catch (error) {
      this.reportError('avatar_runtime_violation', error, { kind, turnId })
      if (kind !== 'failed') {
        this.avatarEventSequence += 1
        try {
          runtime.apply({
            protocol: AGENTOS_AVATAR_RUNTIME_PROTOCOL,
            session_id: runtime.projection.session_id,
            turn_id: turnId,
            sequence: this.avatarEventSequence,
            occurred_at: new Date().toISOString(),
            kind: 'failed',
          })
        } catch { /* fail closed in the renderer through the last projection */ }
      }
    }
    this.invalidate()
  }

  private tryBeginAvatarGreeting(): void {
    if (!this.avatarFirstFrameReady || this.status !== 'ready'
      || this.avatarRuntime?.projection.phase !== 'idle') return
    this.emitAvatarEvent('session_ready', null)
  }

  private scheduleAvatarAudioProgress(turnId: string): void {
    this.clearAvatarAudioProgressTimers()
    const schedule = (delay: number, elapsed: number) => {
      const timer = window.setTimeout(() => {
        if (!this.avatarAudioStarted || this.avatarTurnId !== turnId) return
        this.emitAvatarEvent('audio_progress', turnId, undefined, elapsed)
      }, delay)
      this.avatarAudioProgressTimers.push(timer)
    }
    // Cross the exact contract boundaries using actually elapsed audible time.
    schedule(4_001, 4.001)
    schedule(8_001, 8.001)
  }

  private clearAvatarAudioProgressTimers(): void {
    for (const timer of this.avatarAudioProgressTimers) window.clearTimeout(timer)
    this.avatarAudioProgressTimers = []
  }

  private expectedAudioDurationFromEvent(ev: Record<string, unknown>): number | undefined {
    const direct = ev.expected_audio_duration
    if (typeof direct === 'number' && Number.isFinite(direct) && direct > 0) return direct
    const response = ev.response as Record<string, unknown> | undefined
    const audio = response?.audio as Record<string, unknown> | undefined
    const nested = audio?.duration
    return typeof nested === 'number' && Number.isFinite(nested) && nested > 0
      ? nested
      : undefined
  }

  private responseIdFromEvent(ev: Record<string, unknown>): string | null {
    const response = ev.response as Record<string, unknown> | undefined
    const value = ev.response_id ?? response?.id
    return typeof value === 'string' && value.length > 0 ? value : null
  }

  private beginAvatarAffectRequest(turnId: string, primaryResponseId: string | null): void {
    this.cancelAvatarAffectRequest()
    const dc = this.dc
    if (!dc || !this.avatarAffectFollowup) {
      this.emitAvatarEvent('affect_finished', turnId)
      if (this.avatarTurnId === turnId) this.avatarTurnId = null
      return
    }
    this.avatarAffectRequest = { turnId, primaryResponseId, followupResponseId: null }
    try {
      dc.send(JSON.stringify(this.avatarAffectFollowup))
      this.avatarAffectTimeout = window.setTimeout(() => {
        const pending = this.avatarAffectRequest
        if (!pending || pending.turnId !== turnId) return
        this.avatarAffectRequest = null
        this.avatarAffectTimeout = null
        if (this.avatarRuntime?.projection.phase === 'idle'
          && this.avatarRuntime.projection.turn_id === turnId) {
          this.emitAvatarEvent('affect_finished', turnId)
        }
        if (this.avatarTurnId === turnId) this.avatarTurnId = null
      }, 5_000)
    } catch (error) {
      this.reportError('avatar_affect_followup_send_failed', error, { turnId, primaryResponseId })
      this.avatarAffectRequest = null
      this.emitAvatarEvent('affect_finished', turnId)
      if (this.avatarTurnId === turnId) this.avatarTurnId = null
    }
  }

  private handleAvatarAffectCall(
    callId: string,
    argsJson: string,
    responseId: string | null,
  ): void {
    if (this.acknowledgedAvatarAffectCalls.has(callId)) return
    this.acknowledgedAvatarAffectCalls.add(callId)
    this.sendLocalAvatarAffectAcknowledgement(callId)

    const pending = this.avatarAffectRequest
    if (!pending || !responseId || responseId !== pending.followupResponseId
      || pending.turnId !== this.avatarTurnId
      || this.avatarRuntime?.projection.phase !== 'idle'
      || this.avatarRuntime.projection.turn_id !== pending.turnId) {
      this.reportInfo('avatar_affect_ignored', { callId, responseId })
      return
    }

    let parsed: unknown = null
    try { parsed = JSON.parse(argsJson) } catch { /* normalized to neutral below */ }
    const normalized = normalizeAgentOSAvatarAffect(parsed)
    this.clearAvatarAffectTimeout()
    this.avatarAffectRequest = null
    this.emitAvatarEvent(
      'affect_reported',
      pending.turnId,
      undefined,
      undefined,
      normalized.affect,
      normalized.intensity ?? undefined,
    )
    if (normalized.affect === 'neutral') {
      this.emitAvatarEvent('affect_finished', pending.turnId)
      if (this.avatarTurnId === pending.turnId) this.avatarTurnId = null
    }
  }

  /**
   * Fremdes Werkzeug in der Affect-Antwort: quittieren und begraben.
   *
   * Quittiert wird trotzdem (function_call_output), damit der Call
   * providerseitig nicht offen haengt — aber es folgt WEDER eine
   * Ausfuehrung NOCH ein response.create. Ohne das nachgeschobene
   * response.create entsteht keine zweite gesprochene Antwort; genau das
   * war der Pfad des Bugs.
   *
   * Der Affect-Request wird sofort neutral beendet statt den 5-s-Timeout
   * abzuwarten: Nach einem fremden Werkzeug kommt in dieser Response kein
   * report_affect mehr.
   */
  private discardForeignAvatarToolCall(
    callId: string,
    name: string,
    responseId: string | null,
  ): void {
    this.reportInfo('avatar_affect_foreign_tool_discarded', { tool: name, callId, responseId })
    try {
      this.dc?.send(JSON.stringify({
        type: 'conversation.item.create',
        item: {
          type: 'function_call_output',
          call_id: callId,
          output: JSON.stringify({ discarded: true, reason: 'affect_followup_accepts_only_report_affect' }),
        },
      }))
    } catch (error) {
      this.reportError('avatar_affect_discard_ack_failed', error, { callId, tool: name })
    }
    const pending = this.avatarAffectRequest
    this.cancelAvatarAffectRequest()
    if (pending) {
      this.emitAvatarEvent('affect_finished', pending.turnId)
      if (this.avatarTurnId === pending.turnId) this.avatarTurnId = null
    }
  }

  private sendLocalAvatarAffectAcknowledgement(callId: string): void {
    try {
      this.dc?.send(JSON.stringify({
        type: 'conversation.item.create',
        item: {
          type: 'function_call_output',
          call_id: callId,
          output: JSON.stringify({ accepted: true }),
        },
      }))
    } catch (error) {
      this.reportError('avatar_affect_ack_failed', error, { callId })
    }
  }

  private cancelAvatarAffectRequest(): void {
    this.clearAvatarAffectTimeout()
    this.avatarAffectRequest = null
  }

  private clearAvatarAffectTimeout(): void {
    if (this.avatarAffectTimeout != null) window.clearTimeout(this.avatarAffectTimeout)
    this.avatarAffectTimeout = null
  }

  // ── Internal: tool-call dispatcher ──────────────────────────────────────

  private async runFunctionCall(callId: string, name: string, argsJson: string): Promise<void> {
    const dc = this.dc
    if (!dc) return
    let args: Record<string, unknown> = {}
    try { args = argsJson ? (JSON.parse(argsJson) as Record<string, unknown>) : {} }
    catch (e) { console.warn('[voice-agent] function args parse failed:', e, argsJson) }
    const t0 = Date.now()
    this.reportInfo('tool_call', { tool: name, args })
    if (!this.avatarTurnId) this.avatarTurnId = `turn-${Date.now()}-${++idCounter}`
    this.emitAvatarEvent('tool_started', this.avatarTurnId)

    // GPS source priority: app-provided singleton (e.g. LocationService)
    // > our own watchPosition. The singleton is the canonical source
    // when running inside an app that already tracks position for the
    // map — avoids two parallel watchers + lets a dev-time test
    // position set on the singleton flow to the agent automatically.
    const positionFromSingleton = typeof this.cfg.getPosition === 'function'
      ? this.cfg.getPosition()
      : null
    const effectiveGps = positionFromSingleton || this.gps || null
    const argsWithGps = { ...args, _gps: effectiveGps || undefined }
    let output: unknown
    try {
      const result = await this.core.runToolCall({ name, args: argsWithGps }, async (request) => {
        const res = await fetch(this.cfg.toolUrl, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ tool: request.name, args: request.args }),
        })
        const data = await res.json().catch(() => ({}))
        return res.ok ? data.result : { error: 'tool_failed', status: res.status, detail: data }
      })
      output = result.cleanResult
      const dispatchedCommand = result.commandHandled ? result.command?.name ?? null : null
      // Log AFTER possible strip, but capture dispatchedCommand from BEFORE
      // strip so we can see "did this tool result trigger a command".
      this.reportInfo('tool_result', {
        tool: name,
        ms: Date.now() - t0,
        not_found: !!(output && typeof output === 'object' && (output as Record<string, unknown>).not_found),
        command: dispatchedCommand,
        name_hit: (output && typeof output === 'object'
          ? ((output as Record<string, unknown>).name ?? (output as Record<string, unknown>).station)
          : null) as string | null,
      })

      // Progress: a successful species_lookup is a first-class
      // "user discovered a species" event. Only fire on real hits —
      // not_found means we DIDN'T find it, no progress to record.
      // We denormalize the name at write-time so /progress/summary
      // stays offline-cheap on the server side (per GuideDevBot #1275).
      if (
        (name === 'species_lookup' || name === 'gorge_knowledge_query') &&
        output && typeof output === 'object'
      ) {
        const r = output as Record<string, unknown>
        if (!r.not_found && typeof r.knowledge_id === 'number' && typeof r.name === 'string') {
          trackEvent({
            kind: 'species_found',
            knowledge_id: r.knowledge_id,
            label: r.name,
            payload: { source: 'tool' },
          })
        }
      }
    } catch (e) {
      output = { error: 'tool_network', detail: String(e) }
      this.reportInfo('tool_result', { tool: name, ms: Date.now() - t0, error: String(e) })
    } finally {
      if (this.avatarTurnId) this.emitAvatarEvent('tool_finished', this.avatarTurnId)
      this.invalidate()
    }
    try {
      dc.send(JSON.stringify({
        type: 'conversation.item.create',
        item: {
          type: 'function_call_output',
          call_id: callId,
          output: JSON.stringify(output),
        },
      }))
      dc.send(JSON.stringify({ type: 'response.create' }))
    } catch (e) {
      console.warn('[voice-agent] function_call_output send failed:', e)
    }
  }

  // ── Internal: transcript ────────────────────────────────────────────────

  /** Accumulate token usage from a Realtime `response.done` event's
   *  `usage` field. The shape is:
   *    { total_tokens, input_tokens, output_tokens,
   *      input_token_details:  { cached_tokens, text_tokens, audio_tokens },
   *      output_token_details: { text_tokens, audio_tokens } }
   *  Fields are optional in older/newer API revs — read defensively. */
  private accumulateUsage(usage: Record<string, unknown>): void {
    const inDet = (usage.input_token_details as Record<string, unknown> | undefined) || {}
    const outDet = (usage.output_token_details as Record<string, unknown> | undefined) || {}
    const n = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0)
    this._tokensUsed = {
      audioInputTokens: this._tokensUsed.audioInputTokens + n(inDet.audio_tokens),
      textInputTokens: this._tokensUsed.textInputTokens + n(inDet.text_tokens),
      cachedInputTokens: this._tokensUsed.cachedInputTokens + n(inDet.cached_tokens),
      audioOutputTokens: this._tokensUsed.audioOutputTokens + n(outDet.audio_tokens),
      textOutputTokens: this._tokensUsed.textOutputTokens + n(outDet.text_tokens),
    }
    console.debug(
      '[voice-agent] usage +',
      `audio_in=${n(inDet.audio_tokens)} audio_out=${n(outDet.audio_tokens)} text_in=${n(inDet.text_tokens)} text_out=${n(outDet.text_tokens)}`,
      `→ total cost ≈ €${estimateCostEur(this._tokensUsed).toFixed(4)}`,
    )
  }

  /** Find the bubble with `oldId` and re-assign it the canonical id +
   *  new text in place. Used when we created a temp-id placeholder on
   *  PTT-release and want to merge it with the real Whisper-item_id
   *  without changing array position. */
  private rekeyTranscript(oldId: string, newId: string, text: string): void {
    const idx = this._transcript.findIndex((e) => e.id === oldId)
    if (idx === -1) return
    const next = this._transcript.slice()
    next[idx] = { ...next[idx], id: newId, text, partial: false }
    this._transcript = next
    this.invalidate()
  }

  private removeTranscript(itemId: string): void {
    const idx = this._transcript.findIndex((e) => e.id === itemId)
    if (idx === -1) return
    this._transcript = this._transcript.filter((_, i) => i !== idx)
    this.invalidate()
  }

  private upsertTranscript(itemId: string, speaker: 'user' | 'bot', text: string, partial: boolean): void {
    const idx = this._transcript.findIndex((e) => e.id === itemId)
    if (idx === -1) {
      this._transcript = [...this._transcript, { id: itemId, speaker, text, partial, ts: Date.now() }]
    } else {
      const next = this._transcript.slice()
      next[idx] = { ...next[idx], text, partial }
      this._transcript = next
    }
    this.invalidate()
  }

  private appendTranscript(entry: { speaker: 'user' | 'bot'; text: string; id?: string }): void {
    this._transcript = [
      ...this._transcript,
      { id: entry.id ?? nextId(), speaker: entry.speaker, text: entry.text, ts: Date.now() },
    ]
    this.invalidate()
  }

  // ── Internal: GPS ───────────────────────────────────────────────────────

  private startGpsWatch(): void {
    // If the host app provides a getPosition singleton, defer to it
    // entirely — no internal watchPosition needed. This is the canonical
    // path for apps that already track GPS for their map (factory /
    // manager / controller pattern with a single source of truth).
    // typeof check because Required<> wraps the optional callback as
    // an always-defined function; the runtime value may still be undefined
    // when the host didn't pass one in the constructor.
    if (typeof this.cfg.getPosition === 'function') return
    if (this.gpsWatchId != null) return
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      const gpsParam = params.get('gps')
      if (gpsParam) {
        const [latStr, lonStr] = gpsParam.split(',').map((s) => s.trim())
        const lat = Number(latStr)
        const lon = Number(lonStr)
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          this.gps = { lat, lon }
          console.log('[voice-agent] GPS override (?gps=):', this.gps)
          return
        }
      }
    }
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) return
    try {
      this.gpsWatchId = navigator.geolocation.watchPosition(
        (pos) => { this.gps = { lat: pos.coords.latitude, lon: pos.coords.longitude } },
        (err) => { console.debug('[voice-agent] GPS watch error:', err.message) },
        { enableHighAccuracy: false, maximumAge: 5000, timeout: 8000 },
      )
    } catch { /* noop */ }
  }

  // ── Internal: teardown ──────────────────────────────────────────────────

  private teardown(): void {
    this.clearAvatarAudioProgressTimers()
    this.cancelAvatarAffectRequest()
    if (this.elapsedTick != null) {
      window.clearInterval(this.elapsedTick)
      this.elapsedTick = null
    }
    if (this.dc) {
      try { this.dc.close() } catch { /* noop */ }
      this.dc = null
    }
    if (this.pc) {
      try { this.pc.close() } catch { /* noop */ }
      this.pc = null
    }
    if (this.localStream) {
      for (const t of this.localStream.getTracks()) t.stop()
      this.localStream = null
    }
    if (this.audioEl) {
      try { this.audioEl.srcObject = null } catch { /* noop */ }
      this.audioEl.remove()
      this.audioEl = null
    }
    if (this.levelCtx) {
      void this.levelCtx.close().catch(() => undefined)
      this.levelCtx = null
      this.levelAnalyser = null
      this.levelBuf = null
    }
    if (this.gpsWatchId != null) {
      try { navigator.geolocation.clearWatch(this.gpsWatchId) } catch { /* noop */ }
      this.gpsWatchId = null
    }
    if (this.interruptUnsub) {
      this.interruptUnsub()
      this.interruptUnsub = null
    }
    this.startedAt = null
    this.sessionEntryContext = null
    this.sessionResolvedEntryContext = null
    this.liveTranscript.clear()
    this.pendingUserBubbleId = null
    this._focusedKnowledgeId = null
    this._focusedLocation = null
    this._isBotSpeaking = false
    this.avatarRuntime = null
    this.avatarEventSequence = 0
    this.avatarTurnId = null
    this.avatarAudioStarted = false
    this.avatarFirstFrameReady = false
    this.avatarAffectFollowup = null
    this.avatarActiveResponseId = null
    this.acknowledgedAvatarAffectCalls.clear()
    this.gps = null
  }

  // ── Internal: daily-cap storage ─────────────────────────────────────────

  private loadDailyCounter(): DailyCounter {
    try {
      const raw = localStorage.getItem(this.cfg.storageKey)
      if (!raw) return { date: today(), count: 0 }
      const parsed = JSON.parse(raw) as DailyCounter
      if (parsed.date !== today()) return { date: today(), count: 0 }
      return parsed
    } catch {
      return { date: today(), count: 0 }
    }
  }

  private saveDailyCounter(c: DailyCounter): void {
    try { localStorage.setItem(this.cfg.storageKey, JSON.stringify(c)) }
    catch { /* private mode or quota — ignore */ }
  }
}
